import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { TextField } from "@mui/material";
import MultipleTableDisplayImpl from "../../../components/MultipleTableDisplayImpl";

const MilepostViewer = (props) => {
  const [expresswaysByState, setExpresswaysByState] = useState({});
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedExpressway, setSelectedExpressway] = useState("");
  const [interval, setInterval] = useState(5);
  const [loading, setLoading] = useState(false);
  const [mileposts, setMileposts] = useState(null);

  useEffect(() => {
    fetch(process.env.REACT_APP_API_HOST + "/mileposts/expressways.json")
      .then((res) => res.json())
      .then((result) => {
        if (result.success && result.data) {
          setExpresswaysByState(result.data);
          setStates(Object.keys(result.data).sort());
        }
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    setSelectedExpressway("");
  }, [selectedState]);

  const currentStateInfo = selectedState
    ? expresswaysByState[selectedState]
    : null;
  const currentExpressways = currentStateInfo?.expressways || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedExpressway || !interval) return;

    setLoading(true);
    setMileposts(null);

    const url =
      process.env.REACT_APP_API_HOST +
      `/mileposts/mileposts.json?expressway=${selectedExpressway}&interval=${interval}`;
    fetch(url)
      .then((res) => res.json())
      .then((result) => {
        if (result.success && result.data) {
          setMileposts(result.data);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Typography variant="h5" sx={{ paddingTop: 2, textAlign: "center" }}>
        {" "}
        {module.title}{" "}
      </Typography>
      <Box
        sx={{
          p: 2,
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <form onSubmit={handleSubmit}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel id="state-label">State</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state-select"
                    value={selectedState}
                    label="State"
                    onChange={(e) => setSelectedState(e.target.value)}
                  >
                    {states.map((stateAbbrev) => (
                      <MenuItem key={stateAbbrev} value={stateAbbrev}>
                        {expresswaysByState[stateAbbrev]?.name || stateAbbrev}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth disabled={!selectedState}>
                  <InputLabel id="expressway-label">Expressway</InputLabel>
                  <Select
                    labelId="expressway-label"
                    id="expressway-select"
                    value={selectedExpressway}
                    label="Expressway"
                    onChange={(e) => setSelectedExpressway(e.target.value)}
                  >
                    {currentExpressways.map((exp) => (
                      <MenuItem key={exp.path} value={exp.path}>
                        {exp.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <TextField
                    labelId="interval-label"
                    id="interval-select"
                    value={interval}
                    label="Interval (miles)"
                    onChange={(e) => setInterval(e.target.value)}
                  ></TextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={!selectedExpressway || loading}
                  sx={{ height: 56 }}
                >
                  {loading ? "Loading..." : "Select"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>

        {loading && (
          <Box sx={{ width: "100%", textAlign: "center", mt: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading &&
          mileposts &&
          mileposts.mileposts &&
          mileposts.mileposts.length > 0 && (
            <Box sx={{ flexGrow: 1, overflow: "auto", mt: 2 }}>
              {mileposts.caption && (
                <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                  {mileposts.caption}
                </Typography>
              )}
              <MultipleTableDisplayImpl
                module={module}
                allData={mileposts.mileposts.map((row) => ({
                  ...row,
                  offsetMiles:
                    row.offset != null ? (row.offset / 5280).toFixed(2) : "N/A",
                }))}
                columns={module.tableColumns}
                columnsCard={module.cardColumns}
                cardHeader={module.cardHeader}
                subCardHeader={module.cardSubHeader}
                cardAvatar={module.cardAvatar}
              />
            </Box>
          )}

        {!loading &&
          mileposts &&
          (!mileposts.mileposts || mileposts.mileposts.length === 0) && (
            <Typography sx={{ textAlign: "center", mt: 5 }} variant="h6">
              No milepost data found for the selected expressway.
            </Typography>
          )}
      </Box>
    </>
  );
};
const tableColumns = [
  { field: "milepost", numeric: false, label: "Milepost" },
  { field: "crossStreet", numeric: false, label: "Cross Street" },
  { field: "offsetMiles", numeric: false, label: "Offset (miles)" },
  { field: "municipality", numeric: false, label: "Municipality" },
  { field: "county", numeric: false, label: "County" },
  { field: "idotDistrict", numeric: false, label: "IDOT District" },
];

const cardColumns = [
  { field: "crossStreet", numeric: false, label: "Cross Street" },
  { field: "offsetMiles", numeric: false, label: "Offset (miles)" },
  { field: "municipality", numeric: false, label: "Municipality" },
  { field: "county", numeric: false, label: "County" },
  { field: "idotDistrict", numeric: false, label: "IDOT District" },
];

const MyCardHeader = (props) => {
  const d = props.d;
  return "Milepost " + d.milepost;
};

const module = {
  routeProps: {
    path: "Mileposts",
    component: MilepostViewer,
  },

  name: "Mileposts",
  label: "Mileposts",
  title: " Mileposts ",
  showOnMenu: true,
  tableColumns: tableColumns,
  cardColumns: cardColumns,
  cardHeader: MyCardHeader,
  tabularFormat: true,
  orderBy: null,
};

export default module;
